import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Snackbar from "@material-ui/core/Snackbar";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import GroupIcon from "@material-ui/icons/Group";
import UpdateIcon from "@material-ui/icons/Update";

import MatchNightFormDialog from "./MatchNightFormDialog";
import MatchNightRosterDialog from "./MatchNightRosterDialog";
import { STATUS_LABELS, getServerLocationLabel, getRegionTimeZone } from "./matchNightOptions";
import { formatRegionLabel, regionInputValueToUtcDate, utcDateToRegionInputValue } from "./matchNightTime";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#1C1E20",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.55)",
  },
  tableContainer: {
    backgroundColor: "#262829",
    borderRadius: 12,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      color: "rgba(255,255,255,0.7)",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
  },
  row: {
    "& td": {
      color: "rgba(255,255,255,0.85)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
    },
  },
  mutedText: {
    color: "rgba(255,255,255,0.45)",
    fontSize: "0.82rem",
  },
  dialogPaper: {
    backgroundColor: "#1e2023",
    color: "#fff",
    minWidth: 380,
  },
}));

const STATUS_COLORS = {
  draft: "rgba(255,255,255,0.15)",
  published: "rgba(76,175,80,0.25)",
  live: "rgba(255,193,7,0.3)",
  completed: "rgba(255,255,255,0.1)",
  cancelled: "rgba(244,67,54,0.25)",
};

export default function MatchNightsPage({ catalog }) {
  const classes = useStyles();
  const user = catalog?.user;

  const [nights, setNights] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editingNight, setEditingNight] = useState(null);

  const [rosterNightId, setRosterNightId] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleValue, setRescheduleValue] = useState("");
  const [rescheduling, setRescheduling] = useState(false);

  const [snackbar, setSnackbar] = useState(null);

  const fetchNights = useCallback(async () => {
    if (!user?.userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", rowsPerPage);
      params.set("skip", page * rowsPerPage);

      const url = `${basePath}/api/v7/admin/match-nights/adpass/${user.userId}?${params}`;
      const resp = await fetch(url, { credentials: "include" });
      const data = await resp.json();
      if (data.ok) {
        setNights(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch match nights", err);
    } finally {
      setLoading(false);
    }
  }, [user, page, rowsPerPage]);

  useEffect(() => {
    fetchNights();
  }, [fetchNights]);

  function openCreateDialog() {
    setEditingNight(null);
    setFormOpen(true);
  }

  function openEditDialog(night) {
    setEditingNight(night);
    setFormOpen(true);
  }

  function handleSaved() {
    setFormOpen(false);
    setSnackbar(editingNight ? "Match night updated." : "Match night created.");
    fetchNights();
  }

  function openRescheduleDialog(night) {
    setRescheduleTarget(night);
    setRescheduleValue(utcDateToRegionInputValue(new Date(night.scheduledAt), night.region));
  }

  async function handleReschedule() {
    if (!rescheduleTarget || !rescheduleValue || !user?.userId) return;
    setRescheduling(true);
    try {
      const scheduledAtUtc = regionInputValueToUtcDate(
        rescheduleValue,
        rescheduleTarget.region
      );
      const resp = await fetch(
        `${basePath}/api/v7/admin/match-nights/${rescheduleTarget.id}/reschedule/adpass/${user.userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scheduledAt: scheduledAtUtc ? scheduledAtUtc.toISOString() : null }),
        }
      );
      const data = await resp.json();
      if (data.ok) {
        setRescheduleTarget(null);
        setSnackbar("Match night rescheduled.");
        fetchNights();
      } else {
        setSnackbar(data.message || "Failed to reschedule.");
      }
    } catch (_) {
      setSnackbar("Failed to reschedule.");
    } finally {
      setRescheduling(false);
    }
  }

  async function handleCancelConfirmed() {
    if (!cancelTarget || !user?.userId) return;
    setCancelling(true);
    try {
      const resp = await fetch(
        `${basePath}/api/v7/admin/match-nights/${cancelTarget.id}/cancel/adpass/${user.userId}`,
        { method: "POST", credentials: "include" }
      );
      const data = await resp.json();
      if (data.ok) {
        setCancelTarget(null);
        setSnackbar("Match night cancelled.");
        fetchNights();
      } else {
        setSnackbar(data.message || "Failed to cancel.");
      }
    } catch (_) {
      setSnackbar("Failed to cancel.");
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>Match nights</Typography>
          <Typography className={classes.subtitle}>
            Scheduled sessions players can reserve a spot for. Publish a night to make it visible on
            the public match night page.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          New match night
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress style={{ color: "#7C3AED" }} />
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Scheduled</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Servers</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Confirmed</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Waitlist</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nights.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ color: "rgba(255,255,255,0.4)", padding: "2rem" }}>
                    No match nights yet.
                  </TableCell>
                </TableRow>
              ) : (
                nights.map((night) => (
                  <TableRow key={night.id} className={classes.row}>
                    <TableCell>{formatRegionLabel(new Date(night.scheduledAt), night.region)}</TableCell>
                    <TableCell>{night.region}</TableCell>
                    <TableCell>
                      {night.serverLocations.map(getServerLocationLabel).join(", ")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[night.status] || night.status}
                        size="small"
                        style={{
                          backgroundColor: STATUS_COLORS[night.status] || "rgba(255,255,255,0.1)",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {night.counts.confirmed} / {night.minToFire}-{night.maxCapacity}
                    </TableCell>
                    <TableCell>{night.counts.pending}</TableCell>
                    <TableCell>{night.counts.waitlisted}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(night)} style={{ color: "#A78BFA" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Roster">
                        <IconButton size="small" onClick={() => setRosterNightId(night.id)} style={{ color: "#64B5F6" }}>
                          <GroupIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reschedule">
                        <IconButton size="small" onClick={() => openRescheduleDialog(night)} style={{ color: "#FFB74D" }}>
                          <UpdateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={() => setCancelTarget(night)}
                          disabled={night.status === "cancelled"}
                          style={{ color: night.status === "cancelled" ? "rgba(255,255,255,0.2)" : "#EF5350" }}
                        >
                          <EventBusyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            style={{ color: "rgba(255,255,255,0.7)" }}
          />
        </TableContainer>
      )}

      <MatchNightFormDialog
        open={formOpen}
        night={editingNight}
        user={user}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      <MatchNightRosterDialog
        open={Boolean(rosterNightId)}
        nightId={rosterNightId}
        user={user}
        onClose={() => setRosterNightId(null)}
      />

      {/* Reschedule dialog */}
      <Dialog
        open={Boolean(rescheduleTarget)}
        onClose={() => setRescheduleTarget(null)}
        PaperProps={{ className: classes.dialogPaper }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ color: "#fff" }}>Reschedule match night</DialogTitle>
        <DialogContent>
          <TextField
            label={`New scheduled time (${
              rescheduleTarget
                ? getRegionTimeZone(rescheduleTarget.region).abbreviation
                : "CET"
            })`}
            type="datetime-local"
            value={rescheduleValue}
            onChange={(e) => setRescheduleValue(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            disabled={rescheduling}
            InputLabelProps={{ shrink: true, style: { color: "rgba(255,255,255,0.6)" } }}
            inputProps={{ style: { color: "#fff" } }}
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setRescheduleTarget(null)} disabled={rescheduling} style={{ color: "rgba(255,255,255,0.6)" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReschedule}
            disabled={rescheduling || !rescheduleValue}
          >
            {rescheduling ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        PaperProps={{ className: classes.dialogPaper }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ color: "#fff" }}>Cancel match night?</DialogTitle>
        <DialogContent>
          <Typography style={{ color: "rgba(255,255,255,0.75)" }}>
            {cancelTarget
              ? `This removes ${formatRegionLabel(new Date(cancelTarget.scheduledAt), cancelTarget.region)} from the public page. Reservations stay on record.`
              : ""}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling} style={{ color: "rgba(255,255,255,0.6)" }}>
            Keep it
          </Button>
          <Button variant="contained" color="secondary" onClick={handleCancelConfirmed} disabled={cancelling}>
            {cancelling ? "Cancelling…" : "Cancel match night"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </div>
  );
}

MatchNightsPage.propTypes = {
  catalog: PropTypes.object.isRequired,
};
